import { useState } from 'react';
import { useSMPNavigate } from './hooks/useSMPNavigate';
import { Button } from '@design-system/Button';
import { NavBar } from '@design-system/NavBar';
import { Card, CardContent } from '@design-system/Card';
import { ErrorLoadingPage } from '@/components/screens/ErrorHandling/ErrorLoadingPage';
import { Skeleton } from '@design-system/Skeleton';
import { useSMPData } from './hooks/useSMPData';
import { useMaintenanceGuard } from './hooks/useMaintenanceGuard';
import {
  useGetSubscriptionNextOrderDate,
  useGetChangePlanData,
  useReactivateSubscription,
} from '@viome/viome-base/src/usecase/SMP/SMPQuery';
import { formatSMPDate, isSubscriptionCancelled, formatProductName } from './utils/smpHelpers';
import { SMPMutationErrorDialog, ShimmerImage } from './components';
import { SMP_PATH } from '@/constants/paths';
import { logSMPMutationError } from '@/utils/sentryLogger';

export const SMPReactivateSubscriptionPage = () => {
  const { smpNavigate, smpGoBack } = useSMPNavigate();
  const {
    subscriptionId,
    subscriptionDetail,
    showMaintenance,
    isLoading: isLoadingSMP,
    isError: isErrorSMP,
    isSessionReady,
  } = useSMPData({
    skip: { discounts: true, changePlan: true, uiMessages: true, canSkip: true, holdingPattern: true },
  });

  useMaintenanceGuard(showMaintenance);

  const {
    data: nextOrderDateData,
    isLoading: isLoadingNOD,
  } = useGetSubscriptionNextOrderDate(subscriptionId, {
    enabled: isSessionReady && !!subscriptionId,
  });

  const {
    data: changePlanData,
    isLoading: isLoadingChangePlan,
  } = useGetChangePlanData(subscriptionId, {
    enabled: isSessionReady && !!subscriptionId,
  });

  const reactivateMutation = useReactivateSubscription();
  const [showErrorDialog, setShowErrorDialog] = useState(false);

  const isLoading = isLoadingSMP || isLoadingChangePlan;

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6 p-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full rounded-xl" />
        <Skeleton className="h-12 w-full rounded-lg" />
      </div>
    );
  }

  if (isErrorSMP || !subscriptionDetail) {
    return <ErrorLoadingPage />;
  }

  if (!isSubscriptionCancelled(subscriptionDetail.subscription_status)) {
    smpNavigate(SMP_PATH, { replace: true });
    return null;
  }

  const { line_items } = subscriptionDetail;
  const components = changePlanData?.current_components ?? [];
  const totalPriceCents = line_items.reduce((sum, item) => sum + item.price, 0);
  const totalPriceDollars = (totalPriceCents / 100).toFixed(2);
  const nextOrderDate = nextOrderDateData?.next_order_date
    ? formatSMPDate(nextOrderDateData.next_order_date)
    : null;

  const lineItemBySku = new Map(line_items.map((li) => [li.sku, li]));

  const handleConfirm = () => {
    reactivateMutation.mutate(
      { subscriptionId, source: 'SMP' },
      {
        onSuccess: () => {
          smpNavigate(SMP_PATH, { replace: true });
        },
        onError: (error) => {
          setShowErrorDialog(true);
          logSMPMutationError('reactivateSubscription', error, {
            screen: 'SMPReactivateSubscriptionPage',
            subscriptionId,
          });
        },
      },
    );
  };

  const handleCancel = () => {
    smpGoBack();
  };

  const hasComponents = components.length > 0;
  const hasMultipleLineItems = line_items.length > 1;

  return (
    <div
      className="page-container flex flex-col gap-6 pb-24"
      data-testid="smp-reactivate-page"
    >
      <NavBar
        returnButtonTitle="Back"
        className="md:-ml-9"
        data-testid="smp-reactivate-navbar"
      />

      <h1
        className="typography-h6 text-primary"
        data-testid="smp-reactivate-title"
      >
        Reactivate Subscription
      </h1>

      <Card data-testid="smp-reactivate-card">
        <CardContent className="pt-6">
          <div className="flex flex-col">
            {hasComponents ? (
              components.map((component, index) => {
                const matchedLineItem = lineItemBySku.get(component.sku);
                const itemPrice = matchedLineItem
                  ? `$${(matchedLineItem.price / 100).toFixed(2)}/m`
                  : null;
                const supplyText = component.type === 'KIT' ? 'Included with plan' : '30-day supply';

                return (
                  <div
                    key={component.sku || index}
                    className="flex items-center py-4 first:pt-0"
                    data-testid="smp-reactivate-product-row"
                  >
                    <div className="size-[72px] rounded-xl bg-background-secondary flex items-center justify-center shrink-0 overflow-hidden">
                      {component.image && (
                        <ShimmerImage src={component.image} alt={component.title} containerClassName="size-12" className="size-full object-contain" />
                      )}
                    </div>

                    <div className="flex flex-col ml-5 flex-1 min-w-0">
                      <span className="typography-body font-medium text-primary truncate">
                        {formatProductName(component.title)}
                      </span>
                      <span className="typography-caption1 text-secondary mt-1">
                        {supplyText}
                      </span>
                    </div>

                    {itemPrice && (
                      <span className="typography-body text-primary ml-4 shrink-0">
                        {itemPrice}
                      </span>
                    )}
                  </div>
                );
              })
            ) : (
              line_items.map((item) => {
                const itemPrice = (item.price / 100).toFixed(2);
                return (
                  <div
                    key={item.id}
                    className="flex items-center py-4 first:pt-0"
                    data-testid="smp-reactivate-product-row"
                  >
                    <div className="size-[72px] rounded-xl bg-background-secondary flex items-center justify-center shrink-0 overflow-hidden">
                      {item.image && (
                        <ShimmerImage src={item.image} alt={item.product_name || item.title} containerClassName="size-12" className="size-full object-contain" />
                      )}
                    </div>

                    <div className="flex flex-col ml-5 flex-1 min-w-0">
                      <span className="typography-body font-medium text-primary truncate">
                        {formatProductName(item.product_name || item.title)}
                      </span>
                      <span className="typography-caption1 text-secondary mt-1">
                        30-day supply
                      </span>
                    </div>

                    {hasMultipleLineItems && (
                      <span className="typography-body text-primary ml-4 shrink-0">
                        ${itemPrice}/m
                      </span>
                    )}
                  </div>
                );
              })
            )}

            <div className="border-t border-stroke-secondary-subtle my-2" />

            <div className="flex items-start justify-end py-2">
              <div className="flex flex-col items-end">
                <div className="flex items-center gap-4">
                  <span className="typography-caption1 text-secondary uppercase tracking-wide">
                    Total
                  </span>
                  <span className="typography-h3 text-primary">
                    ${totalPriceDollars}/m
                  </span>
                </div>
                <span className="typography-caption2 text-tertiary mt-1">
                  (plus tax, if applicable)
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {isLoadingNOD ? (
        <Skeleton className="h-12 w-full" />
      ) : (
        nextOrderDate && (
          <p
            className="typography-body text-primary mt-4"
            data-testid="smp-reactivate-summary"
          >
            Your next order will be placed on {nextOrderDate}. If you'd like
            to change your plan, you may do so after your subscription has
            reactivated.
          </p>
        )
      )}

      <div className="flex flex-col gap-4 max-w-sm mt-4">
        <Button
          onClick={handleConfirm}
          disabled={reactivateMutation.isPending}
          data-testid="smp-reactivate-confirm"
        >
          {reactivateMutation.isPending ? 'Reactivating...' : 'Confirm'}
        </Button>

        <Button
          variant="secondary"
          onClick={handleCancel}
          disabled={reactivateMutation.isPending}
          data-testid="smp-reactivate-cancel"
        >
          Cancel
        </Button>
      </div>

      <SMPMutationErrorDialog
        open={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        description="Something went wrong while reactivating your subscription. Please try again."
      />
    </div>
  );
};

